<script setup lang="ts">
    import { ref, computed, watch, provide } from 'vue';
    import { RouterView, useRoute } from 'vue-router';
    import ContactList from '@views/Lists/ContactList.vue';
    import type { ContactsResponse } from '@views/Lists/ContactList.access';
    import {
        getAllContacts,
        exportAllContactsExcel,
        exportAllContactsCsv,
        exportAllContactsToNewList,
        exportAllContactsToExistingList,
        exportAllContactsByMail,
    } from './Contacts.access';
    import { loadContact } from '@views/Contact/Contact.access';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatNumericText } from '@shared/utils/formatNumericText';

    const route = useRoute();
    const showingContact = computed(() => route.name === 'ContactInContacts');

    const lastViewedContactId = ref<string | null>(null);
    const contactWasUpdated = ref(false);

    provide('markContactUpdated', () => {
        contactWasUpdated.value = true;
    });

    watch(
        () => route.params.contactId as string | undefined,
        (id) => {
            if (id) {
                lastViewedContactId.value = id;
                contactWasUpdated.value = false;
            }
        }
    );

    watch(showingContact, async (showing, wasShowing) => {
        if (wasShowing && !showing && lastViewedContactId.value && contactWasUpdated.value) {
            const updated = await loadContact(lastViewedContactId.value);
            contactListRef.value?.updateContact(updated);
        }
    });

    let fetchAbortController: AbortController | null = null;

    async function fetchContacts(skip: number, pageSize: number, extra: string): Promise<ContactsResponse> {
        fetchAbortController?.abort();
        fetchAbortController = new AbortController();
        return getAllContacts(skip, pageSize, extra, fetchAbortController.signal);
    }

    async function exportExcel(extra: string): Promise<{ Url: string }> {
        return exportAllContactsExcel(extra);
    }

    async function exportCsv(extra: string): Promise<{ Url: string }> {
        return exportAllContactsCsv(extra);
    }

    async function exportToNewList(extra: string): Promise<{ ListId: string }> {
        return exportAllContactsToNewList(extra);
    }

    async function exportToExistingList(extra: string, targetListId: string): Promise<{ ListId: string }> {
        return exportAllContactsToExistingList(extra, targetListId);
    }

    async function sendExcelByMail(extra: string): Promise<void> {
        return exportAllContactsByMail(extra);
    }

    const contactListRef = ref<InstanceType<typeof ContactList> | null>(null);

    const newVersionAlertContent = () => {
        const url = `/Contacts/Old`;
        return $Resources.GeneralNewVersionAlertContent.replace('{{', `<a href="${url}">`).replace('}}', '</a>');
    };
</script>

<template>
    <RouterView v-if="showingContact" />

    <div v-show="!showingContact">
        <BsAlert class="m-0-b" :type="AlertTypes.Info" :title="$Resources.GeneralNewVersionAlertTitle" :content="newVersionAlertContent()" />

        <nav class="navbar main-navbar p-relative" role="navigation">
            <div class="container-fluid">
                <p class="navbar-title">
                    {{ $Resources.ContactsHeading }}
                </p>
            </div>
        </nav>

        <div class="list-header p-0-b b-0-b">
            <h1>
                {{ $Resources.AllContactsHeading }}
                <small>
                    {{ formatNumericText(contactListRef?.totalCount ?? 0, { numberWithSpace: true }) }}
                </small>
            </h1>
            <p>{{ $Resources.AllContactsBody }}</p>
        </div>

        <ContactList
            ref="contactListRef"
            :isCheckable="true"
            :fetchContacts="fetchContacts"
            :exportExcel="exportExcel"
            :exportCsv="exportCsv"
            :exportToNewList="exportToNewList"
            :exportToExistingList="exportToExistingList"
            :sendExcelByMail="sendExcelByMail"
            :hideAddedDateTime="true"
            :contactRoute="(contactId) => ({ name: 'ContactInContacts', params: { contactId } })"
            :odataV4="true" />
    </div>
</template>
